/**
 * GI dashboard motion library.
 *
 * Every animation in `ui/` maps to one of nine named verbs defined here.
 * Keeping them in a single module gives the dashboard a single, testable
 * motion language — page authors never write a magic number, they pick a
 * verb. See [docs/DASHBOARD-REDESIGN-SPEC.md §9.1].
 *
 * Usage patterns:
 *   - `arrive`, `depart`, `settle`, `stagger`, `breathe`, `flash` are
 *     [framer-motion `Variants`](https://www.framer.com/motion/animation/)
 *     objects. Use them with `<motion.* variants={X} initial="hidden"
 *     animate="visible" />`.
 *   - `lift` and `press` are TargetAndTransition objects, designed for
 *     `whileHover` / `whileTap` props on a `<motion.button>` /
 *     `<motion.article>`.
 *   - `track` is a small style+transition spec for a 1px line that
 *     follows scroll (or any other motion-value); pair it with
 *     `useScroll` + `useTransform` in the consuming component.
 *
 * Timing curves are defined once at the top of the file. `EMPHASIZED`
 * mirrors the Material 3 / Linear emphasized ease used across the
 * marketing site so the dashboard feels like the same product.
 */

import type { TargetAndTransition, Transition, Variants } from 'framer-motion';

/* eslint-disable @typescript-eslint/consistent-type-assertions */
const EMPHASIZED: [number, number, number, number] = [0.2, 0.0, 0.0, 1.0];
const EASE_OUT: [number, number, number, number] = [0.0, 0.0, 0.2, 1.0];
const EASE_IN: [number, number, number, number] = [0.4, 0.0, 1.0, 1.0];
const EASE_IN_OUT: [number, number, number, number] = [0.4, 0.0, 0.2, 1.0];
/* eslint-enable @typescript-eslint/consistent-type-assertions */

/**
 * `arrive` — element enters the viewport. Scale 95→100 + opacity 0→1
 * over 350ms emphasized. Used for: cards, modals, proof-card galleries,
 * status surfaces.
 */
export const arrive: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.35, ease: EMPHASIZED },
  },
};

/**
 * `depart` — element leaves. Scale 100→95 + opacity 1→0 over 200ms
 * ease-in. Use as the `exit` prop on `<motion.* />` inside an
 * `<AnimatePresence>`.
 */
export const depart: Variants = {
  visible: { opacity: 1, scale: 1 },
  hidden: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2, ease: EASE_IN },
  },
};

/**
 * `lift` — hover state. translate-y −2px, shadow + indigo bloom
 * intensify. Apply via `whileHover={lift}` on a `<motion.* />`.
 *
 * The boxShadow uses the dashboard's indigo brand token (rgb 94 106 210)
 * at 18% opacity — a softer halo than a generic neutral shadow, so the
 * lifted card feels lit from the GI brand rather than from a pane of
 * grey glass.
 */
export const lift: TargetAndTransition = {
  y: -2,
  boxShadow: '0 8px 24px rgba(94, 106, 210, 0.18)',
  transition: { duration: 0.15, ease: EASE_OUT },
};

/**
 * `press` — click feedback. Scale 100→97 over 80ms. Apply via
 * `whileTap={press}` on a `<motion.* />` that is itself a button.
 */
export const press: TargetAndTransition = {
  scale: 0.97,
  transition: { duration: 0.08, ease: EASE_OUT },
};

/**
 * `settle` — first-paint hero arrival with a 4px y-bounce that
 * oscillates ~1.5 times before settling, ~350ms total. Use for the
 * hero block and the post-sign celebration moment, where a touch of
 * organic overshoot reads as confidence rather than as glitch.
 */
export const settle: Variants = {
  hidden: { opacity: 0, y: 4 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 420,
      damping: 22,
      mass: 0.6,
    } satisfies Transition,
  },
};

/**
 * `stagger` — applies `arrive` to each child with a 40ms delay
 * between siblings. Set as the parent's `variants` and let the children
 * inherit `arrive` + `initial`/`animate`. Use for the proof gallery, the
 * recipe grid, the recent-proofs card list.
 */
export const stagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.05,
    },
  },
};

/**
 * `track` — a 1px line that follows scroll position with no snap.
 * Used for: year bands in the Vault, navigation indicators in the
 * page header.
 *
 * This export is the *style + transition spec* a consumer applies to
 * a `<motion.div style={...}>`. Pair it with `useScroll()` +
 * `useTransform()` in the consuming component to drive the y position
 * from the page's scroll progress. See spec §9.1 / §5.4 for the
 * reference layout.
 */
export const track = {
  className: 'absolute start-0 top-0 w-px bg-spark/50',
  transition: { duration: 0 } satisfies Transition,
} as const;

/**
 * `breathe` — slow scale pulse 100→104→100 over 4 seconds, infinite.
 * Used for the *witnessing* status pill: a proof that has been signed
 * locally but is awaiting external attestation. Stops on success.
 */
export const breathe: Variants = {
  rest: { scale: 1 },
  on: {
    scale: [1, 1.04, 1],
    transition: {
      duration: 4,
      ease: EASE_IN_OUT,
      repeat: Infinity,
    } satisfies Transition,
  },
};

/**
 * `flash` — 600ms decaying border highlight at the brand accent.
 * Fired when a connector OAuth completes, when a sign event lands,
 * when a successful state-change wants a momentary celebration that
 * is not loud enough to interrupt the flow.
 */
export const flash: Variants = {
  off: { borderColor: 'rgba(94, 106, 210, 0)' },
  on: {
    borderColor: [
      'rgba(94, 106, 210, 0)',
      'rgba(94, 106, 210, 0.9)',
      'rgba(94, 106, 210, 0)',
    ],
    transition: { duration: 0.6, ease: EASE_OUT } satisfies Transition,
  },
};
